import { useMemo, useRef, useState } from 'react';

import { createAutocomplete } from '@algolia/autocomplete-core';
import { getAlgoliaResults } from '@algolia/autocomplete-js';
import { useSearchBox } from 'react-instantsearch';

import { useSearchClient } from '../provider/searchClientProvider';
import { INSTANT_SEARCH_API_ERRORS } from '../errors/constants';
import localStorageAvailable from '@javascript/helpers/localStorage';

import type { AutocompleteState, BaseItem } from '@algolia/autocomplete-core';

const RECENT_QUERIES_KEY = 'recentQueries';

const addQueryToLocalStorage = (query: string | null) => {
  if (!localStorageAvailable() || !query) return;

  const queries = JSON.parse(
    localStorage.getItem(RECENT_QUERIES_KEY) || '[]'
  ) as string[];

  // add query to front of the list
  queries.unshift(query);
  // remove duplicates
  const uniqueQueries = Array.from(new Set(queries));
  // limit to 5 queries
  if (uniqueQueries.length > 5) {
    uniqueQueries.splice(5);
  }
  // update localStorage
  localStorage.setItem(RECENT_QUERIES_KEY, JSON.stringify(uniqueQueries));
};

interface UseAutoCompleteReturnValue {
  autoComplete: ReturnType<typeof createAutocomplete>;
  autoCompleteError: (typeof INSTANT_SEARCH_API_ERRORS)[number] | null;
  autoCompleteState: AutocompleteState<BaseItem>;
  clearAutoComplete: VoidFunction;
  closeSuggestions: VoidFunction;
}

export default function useAutoComplete(
  onSuggestionSelect: (query: string | null) => void,
  onInputChange?: (query: string) => void
): UseAutoCompleteReturnValue {
  const { autocompleteIndexName, searchClient } = useSearchClient();
  const { query } = useSearchBox();

  const [inputValue, setInputValue] = useState(query || '');
  const [autoCompleteError, setAutoCompleteError] = useState<
    (typeof INSTANT_SEARCH_API_ERRORS)[number] | null
  >(null);
  const [autoCompleteState, setAutoCompleteState] = useState(
    {} as AutocompleteState<BaseItem>
  );

  const clearingRef = useRef(false);

  const searchClientHandlingErrors: typeof searchClient = {
    ...searchClient,
    //@ts-expect-error overwriting the search method to catch errors
    search(queries) {
      return searchClient.search(queries).catch((error) => {
        const instantSearchError = INSTANT_SEARCH_API_ERRORS.find(
          (err) => error.message === err.instantSearchError
        );
        if (instantSearchError) {
          setAutoCompleteError(instantSearchError);
        }
        return { results: [{ hits: [] }] };
      });
    },
  };

  const autoComplete = useMemo(
    () =>
      createAutocomplete({
        openOnFocus: true,
        onSubmit({ state }) {
          const { query } = state;
          addQueryToLocalStorage(query);
          onSuggestionSelect(query);
        },
        insights: true,
        initialState: { query },
        onStateChange({ state }) {
          // hide the error message if the user clears the query
          if (state.query === '' && autoCompleteError) {
            setAutoCompleteError(null);
          }
          setAutoCompleteState(state);

          // prevent duplicate input change when clearing
          if (clearingRef.current) {
            if (onInputChange) onInputChange('');
            return;
          }

          // only call onInputChange if query actually changed
          if (onInputChange && state.query !== inputValue) {
            onInputChange(state.query);
          }
        },
        getSources() {
          return [
            {
              sourceId: 'Recent searches',
              getItemInputValue({ item }) {
                return item.query as string;
              },
              getItems(state) {
                // If there's a query present, don't show recent queries
                if (state.query) return [];

                const recentQueries = JSON.parse(
                  localStorage.getItem(RECENT_QUERIES_KEY) || '[]'
                ) as string[];

                return recentQueries.map((query) => ({ query })) as BaseItem[];
              },
              getItemUrl({ item }) {
                return item.url as string;
              },
              onSelect({ item }) {
                addQueryToLocalStorage(item.query as string);
                onSuggestionSelect(item.query as string);
                setInputValue(item.query as string);
                autoComplete.setQuery(item.query as string);
                if (onInputChange) onInputChange(item.query as string);
              },
            },
            {
              sourceId: 'Trending searches',
              getItemInputValue({ item }) {
                return item.query as string;
              },
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient: searchClientHandlingErrors,
                  queries: [
                    {
                      indexName: `${autocompleteIndexName}_query_suggestions`,
                      params: {
                        query,
                        hitsPerPage: query ? 8 : 5,
                      },
                    },
                  ],
                });
              },
              getItemUrl({ item }) {
                return item.url as string;
              },
              onSelect({ item }) {
                addQueryToLocalStorage(item.query as string);
                onSuggestionSelect(item.query as string);
                setInputValue(item.query as string);
                autoComplete.setQuery(item.query as string);
                if (onInputChange) onInputChange(item.query as string);
              },
            },
            {
              sourceId: 'Suggested pages',
              getItemInputValue({ item }) {
                return item.query as string;
              },
              getItems({ query }) {
                if (query.length < 4) return [];

                return getAlgoliaResults({
                  searchClient: searchClientHandlingErrors,
                  queries: [
                    {
                      indexName: 'suggested_pages',
                      params: {
                        query,
                      },
                    },
                  ],
                });
              },
              getItemUrl({ item }) {
                return item.url as string;
              },
              onSelect({ item }) {
                window.location.href = item.url as string;
              },
            },
          ];
        },
      }),
    [query]
  );

  const closeSuggestions = () => {
    autoComplete.setIsOpen(false);
    onInputChange?.(inputValue);
  };

  // visually clear the input and Algolia query without triggering unwanted onStateChange side effects
  // clearingRef prevents onStateChange from resetting the input value or firing onInputChange during this process
  const clearAutoComplete = () => {
    clearingRef.current = true;
    setInputValue('');
    autoComplete.setQuery('');
    onInputChange?.('');
    setAutoCompleteError(null);
    setTimeout(() => {
      clearingRef.current = false;
    });
  };

  //monkeypatch autocomplete.getInputProps to ensure the cursor is at the end of the input on safari
  const extendedGetInputProps = (
    props: Parameters<typeof autoComplete.getInputProps>[0]
  ) => {
    const { onFocus, ...rest } = autoComplete.getInputProps(props);
    return {
      ...rest,
      value: inputValue,
      onChange: (e: Event) => {
        const target = e.target as HTMLInputElement;
        setInputValue(target.value);
        if (rest.onChange) rest.onChange(e);
      },
      onFocus: (e: Event) => {
        // call the original onFocus handler
        onFocus(e);

        // set the cursor to the end of the input
        const input = e.currentTarget as HTMLInputElement;
        const val = input.value;
        input.setSelectionRange(val.length, val.length);
      },
    };
  };

  return {
    autoComplete: {
      ...autoComplete,
      getInputProps: extendedGetInputProps,
    },
    autoCompleteError,
    autoCompleteState,
    clearAutoComplete,
    closeSuggestions,
  };
}
